import React, { useState, useEffect } from 'react';
import axios from 'axios';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Avatar,
  Tooltip,
  TablePagination,
  Fade,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Stack,
  LinearProgress,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Search as SearchIcon,
  EmojiEvents as TrophyIcon,
  Person as PersonIcon,
  TrendingUp as TrendingUpIcon,
  Info as InfoIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';

// components
import { Typography } from '../../../components/Wrappers';
import AlertModal from '../../../components/AlertModal';
import ConfirmModal from '../../../components/ConfirmModal';
import RefreshButton from '../../../components/RefreshButton/RefreshButton';

const GlassCard = ({ children, sx, ...props }) => (
  <Card
    {...props}
    sx={{
      background: 'rgba(255, 255, 255, 0.7)',
      backdropFilter: 'blur(20px)',
      borderRadius: '16px',
      border: '1px solid rgba(255, 255, 255, 0.3)',
      boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.05)',
      ...sx,
    }}
  >
    {children}
  </Card>
);

const RewardApprove = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [achievements, setAchievements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('action_required');
  
  const [detailModal, setDetailModal] = useState({ open: false, data: null });
  const [approveModal, setApproveModal] = useState({ open: false, data: null, remark: '' });
  const [alertData, setAlertData] = useState({ open: false, message: '', type: 'info' });

  useEffect(() => {
    fetchAchievements();
  }, [page, rowsPerPage, search, statusFilter]);

  const handleRefresh = () => {
    fetchAchievements();
  };

  const fetchAchievements = async () => {
    try {
      setLoading(true);
      const baseURL = process.env.REACT_APP_API_BASE_URL || '';
      const response = await axios.get(`${baseURL}/api/rewards/rewardAchievementList`, {
        params: {
          page: page + 1,
          limit: rowsPerPage,
          search,
          status: statusFilter === 'all' ? '' : statusFilter
        }
      });

      if (response.data.status) {
        setAchievements(response.data.data);
        setTotalCount(response.data.extra?.total || response.data.data.length);
      }
    } catch (error) {
      console.error('Error fetching achievements:', error);
      setAlertData({ open: true, message: 'Failed to fetch achievement list', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    try {
      const baseURL = process.env.REACT_APP_API_BASE_URL || '';
      const isReturn = approveModal.data?.status === 'return_pending';
      const endpoint = isReturn 
        ? `${baseURL}/api/rewards/confirmRewardReturn` 
        : `${baseURL}/api/rewards/completeRewardAchievement`;
      
      const payload = {
        achievementId: approveModal.data._id,
        remark: isReturn 
          ? (approveModal.remark || 'Returned confirmed by admin') 
          : approveModal.remark
      };

      const response = await axios.put(endpoint, payload);

      if (response.data.status) {
        setAlertData({ 
          open: true, 
          message: isReturn 
            ? 'Reward return confirmed successfully' 
            : 'Reward approved and completed successfully', 
          type: 'success' 
        });
        setApproveModal({ open: false, data: null, remark: '' });
        fetchAchievements();
      }
    } catch (error) {
      console.error('Error in approval:', error);
      setAlertData({ 
        open: true, 
        message: approveModal.data?.status === 'return_pending'
          ? 'Failed to confirm reward return'
          : 'Failed to complete reward approval', 
        type: 'error' 
      });
    }
  };

  const getProgressColor = (achieved, target) => {
    const percent = (achieved / target) * 100;
    if (percent >= 100) return theme.palette.success.main;
    if (percent >= 80) return theme.palette.warning.main;
    return theme.palette.error.main;
  };

  return (
    <Box sx={{ pt: { xs: 1, md: 2 }, px: { xs: 2, md: 4 }, pb: { xs: 2, md: 4 }, minHeight: '100vh', background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)' }}>
      <Box sx={{
        mb: 4,
        display: 'flex',
        flexDirection: { xs: 'column', sm: 'row' },
        justifyContent: 'space-between',
        alignItems: { xs: 'flex-start', sm: 'center' },
        gap: 2
      }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 800, color: 'text.primary', fontSize: { xs: '1.5rem', md: '2rem' }, mb: 0.5 }}>
            Reward Approval
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
            Review and approve rewards for associates who have met their performance targets.
          </Typography>
        </Box>
        <RefreshButton onClick={handleRefresh} loading={loading} />
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {[
          { label: 'Total Achievements', value: totalCount, icon: <TrophyIcon />, color: theme.palette.primary.main },
          { label: 'Pending Approval', value: achievements.filter(a => a.status === 'pending' || a.status === 'action_required' || a.status === 'return_pending').length, icon: <InfoIcon />, color: theme.palette.warning.main },
          { label: 'Approved Rewards', value: achievements.filter(a => a.status === 'completed').length, icon: <CheckIcon />, color: theme.palette.success.main },
          { label: 'Total Amount', value: `₹${achievements.reduce((acc, curr) => acc + (curr.rewardAmount || 0), 0).toLocaleString()}`, icon: <TrendingUpIcon />, color: theme.palette.info.main },
        ].map((stat, i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <GlassCard>
              <CardContent sx={{ display: 'flex', alignItems: 'center', p: 2.5 }}>
                <Avatar sx={{ 
                  background: `linear-gradient(135deg, ${stat.color} 0%, ${stat.color}99 100%)`,
                  color: '#fff',
                  width: 48, height: 48, 
                  borderRadius: '12px',
                  boxShadow: `0 4px 12px 0 ${stat.color}30`
                }}>
                  {stat.icon}
                </Avatar>
                <Box sx={{ ml: 2 }}>
                  <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: 0.5 }}>{stat.label.toUpperCase()}</Typography>
                  <Typography variant="h5" sx={{ fontWeight: 800, color: 'text.primary' }}>{stat.value}</Typography>
                </Box>
              </CardContent>
            </GlassCard>
          </Grid>
        ))}
      </Grid>

      {/* Filters */}
      <GlassCard sx={{ mb: 4, p: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search associate name or code..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ color: 'text.secondary', mr: 1, fontSize: 20 }} />,
              }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: 'rgba(255,255,255,0.5)' } }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              select
              fullWidth
              size="small"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              SelectProps={{ native: true }}
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', bgcolor: 'rgba(255,255,255,0.5)' } }}
            >
              <option value="action_required">Action Required</option>
              <option value="pending">Pending Approval</option>
              <option value="return_pending">Return Pending</option>
              {/* <option value="all">All</option> */}
            </TextField>
          </Grid>
        </Grid>
      </GlassCard>

      {/* List */}
      <Paper sx={{ 
        borderRadius: '16px', 
        overflow: 'hidden', 
        background: 'rgba(255, 255, 255, 0.7)',
        backdropFilter: 'blur(20px)',
        border: '1px solid rgba(255, 255, 255, 0.3)',
        boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.05)'
      }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 8 }}>
            <CircularProgress color="primary" />
          </Box>
        ) : (
          <TableContainer sx={{ maxHeight: 600 }}>
            <Table stickyHeader>
              <TableHead sx={{ bgcolor: 'rgba(244, 247, 254, 0.5)' }}>
                <TableRow>
                  <TableCell sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.75rem' }}>ASSOCIATE</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.75rem' }}>TARGET LEVEL</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.75rem' }}>BUSINESS ACHIEVEMENT</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.75rem' }}>REWARD</TableCell>
                  <TableCell sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.75rem' }}>STATUS</TableCell>
                  <TableCell align="right" sx={{ fontWeight: 700, color: 'text.secondary', fontSize: '0.75rem' }}>ACTIONS</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {achievements.map((item) => (
                  <TableRow key={item._id} sx={{ '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.02)' }, transition: '0.2s' }}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar sx={{ bgcolor: theme.palette.primary.main, width: 36, height: 36, mr: 1.5, fontSize: '0.9rem', fontWeight: 800 }}>
                          {item.associateName?.charAt(0)}
                        </Avatar>
                        <Box>
                          <Typography sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.9rem' }}>{item.associateName}</Typography>
                          <Typography variant="caption" sx={{ color: 'text.secondary' }}>Code: {item.associateCode}</Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontWeight: 800, color: 'primary.main', fontSize: '0.85rem' }}>{item.levelCode}</Typography>
                      <Typography variant="caption" sx={{ display: 'block', fontWeight: 600 }}>{item.rankName}</Typography>
                    </TableCell>
                    <TableCell sx={{ minWidth: 200 }}>
                      <Box sx={{ mb: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="caption" sx={{ fontWeight: 700 }}>Team Business</Typography>
                          {/* <Typography variant="caption" sx={{ fontWeight: 700 }}>{item.teamBusinessTarget}%</Typography> */}
                        </Box>
                        <LinearProgress 
                          variant="determinate" 
                          value={Math.min(100, (item.teamBusinessAchieved / item.teamBusinessTarget) * 100)} 
                          sx={{ height: 6, borderRadius: 3, bgcolor: 'rgba(0,0,0,0.05)', '& .MuiLinearProgress-bar': { bgcolor: getProgressColor(item.teamBusinessAchieved, item.teamBusinessTarget) } }}
                        />
                      </Box>
                      <Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                          <Typography variant="caption" sx={{ fontWeight: 700 }}>Self Business</Typography>
                          {/* <Typography variant="caption" sx={{ fontWeight: 700 }}>{Math.round((item.selfBusinessAchieved / item.selfBusinessTarget) * 100)}%</Typography> */}
                        </Box>
                        <LinearProgress 
                          variant="determinate" 
                          value={Math.min(100, (item.selfBusinessAchieved / item.selfBusinessTarget) * 100)} 
                          sx={{ height: 6, borderRadius: 3, bgcolor: 'rgba(0,0,0,0.05)', '& .MuiLinearProgress-bar': { bgcolor: getProgressColor(item.selfBusinessAchieved, item.selfBusinessTarget) } }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontWeight: 700, color: 'text.primary', fontSize: '0.85rem' }}>{item.rewardItem}</Typography>
                      <Typography variant="caption" color="primary" sx={{ fontWeight: 700 }}>₹{item.rewardAmount?.toLocaleString()}</Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={item.status?.toUpperCase()?.replace('_', ' ')} 
                        size="small"
                        sx={{ 
                          fontWeight: 800, 
                          fontSize: '0.65rem',
                          bgcolor: item.status === 'completed' 
                            ? `${theme.palette.success.main}15` 
                            : item.status === 'return_pending'
                              ? `${theme.palette.error.main}15`
                              : `${theme.palette.warning.main}15`,
                          color: item.status === 'completed' 
                            ? theme.palette.success.main 
                            : item.status === 'return_pending'
                              ? theme.palette.error.main
                              : theme.palette.warning.main,
                          border: `1px solid ${
                            item.status === 'completed' 
                              ? theme.palette.success.main 
                              : item.status === 'return_pending'
                                ? theme.palette.error.main
                                : theme.palette.warning.main
                          }30`
                        }} 
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={1} justifyContent="flex-end">
                        <IconButton onClick={() => setDetailModal({ open: true, data: item })} size="small" color="primary">
                          <ViewIcon fontSize="small" />
                        </IconButton>
                        {(item.status === 'pending' || item.status === 'action_required' || item.status === 'return_pending') && (
                          <Button
                            size="small"
                            variant="contained"
                            onClick={() => setApproveModal({ open: true, data: item, remark: '' })}
                            sx={{ 
                              borderRadius: '8px', 
                              textTransform: 'none', 
                              fontWeight: 700,
                              bgcolor: theme.palette.success.main,
                              '&:hover': { bgcolor: theme.palette.success.dark }
                            }}
                          >
                            {item.status === 'return_pending' ? 'Approve Return' : 'Approve'}
                          </Button>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
                {achievements.length === 0 && !loading && (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 8 }}>
                      <Typography color="text.secondary">No reward achievements found.</Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
        <TablePagination
          rowsPerPageOptions={[5, 10, 15]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={(e, p) => setPage(p)}
          onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
        />
      </Paper>

      {/* Approve Modal */}
      <Dialog open={approveModal.open} onClose={() => setApproveModal({ ...approveModal, open: false })} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 800 }}>
          {approveModal.data?.status === 'return_pending' ? 'Confirm Reward Return' : 'Approve Reward'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2, color: 'text.secondary' }}>
            {approveModal.data?.status === 'return_pending' ? (
              <>
                Are you sure you want to confirm the return of <strong>{approveModal.data?.rewardItem}</strong> reward for <strong>{approveModal.data?.associateName}</strong>?
              </>
            ) : (
              <>
                Are you sure you want to approve the <strong>{approveModal.data?.rewardItem}</strong> reward for <strong>{approveModal.data?.associateName}</strong>?
              </>
            )}
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={3}
            label={approveModal.data?.status === 'return_pending' ? 'Remarks (Optional, default: Returned confirmed by admin)' : 'Remarks (Optional)'}
            placeholder="Add any internal remarks here..."
            value={approveModal.remark}
            onChange={(e) => setApproveModal({ ...approveModal, remark: e.target.value })}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setApproveModal({ ...approveModal, open: false })}>Cancel</Button>
          <Button onClick={handleApprove} variant="contained" color="success" sx={{ fontWeight: 700 }}>
            {approveModal.data?.status === 'return_pending' ? 'Confirm Return' : 'Confirm Approval'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Detail Modal */}
      <Dialog open={detailModal.open} onClose={() => setDetailModal({ open: false, data: null })} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 800, display: 'flex', alignItems: 'center', gap: 1 }}>
          <TrophyIcon color="primary" /> Reward Achievement Details
        </DialogTitle>
        <DialogContent dividers>
          {detailModal.data && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Avatar sx={{ width: 56, height: 56, mr: 2, bgcolor: 'primary.main' }}><PersonIcon /></Avatar>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>{detailModal.data.associateName}</Typography>
                    <Typography variant="body2" color="text.secondary">{detailModal.data.associateEmail} | {detailModal.data.associatePhone}</Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>ACHIEVED LEVEL</Typography>
                <Typography sx={{ fontWeight: 700 }}>{detailModal.data.levelCode} - {detailModal.data.rankName}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>ACHIEVEMENT DATE</Typography>
                <Typography sx={{ fontWeight: 700 }}>{new Date(detailModal.data.achievementDate).toLocaleDateString()}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Divider sx={{ my: 1 }} />
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>TEAM BUSINESS</Typography>
                <Typography sx={{ fontWeight: 700 }}>₹{detailModal.data.teamBusinessAchieved.toLocaleString()} / ₹{detailModal.data.teamBusinessTarget.toLocaleString()}</Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>SELF BUSINESS</Typography>
                <Typography sx={{ fontWeight: 700 }}>₹{detailModal.data.selfBusinessAchieved.toLocaleString()} / ₹{detailModal.data.selfBusinessTarget.toLocaleString()}</Typography>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ p: 2, bgcolor: '#f0f7ff', borderRadius: 2 }}>
                  <Typography variant="caption" color="primary" sx={{ fontWeight: 800 }}>REWARD DETAILS</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 800, mt: 0.5 }}>{detailModal.data.rewardItem}</Typography>
                  <Typography variant="body2">{detailModal.data.rewardDescription}</Typography>
                  <Typography variant="h6" color="primary" sx={{ fontWeight: 800, mt: 1 }}>₹{detailModal.data.rewardAmount.toLocaleString()}</Typography>
                </Box>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setDetailModal({ open: false, data: null })} variant="contained" fullWidth>Close</Button>
        </DialogActions>
      </Dialog>

      <AlertModal open={alertData.open} message={alertData.message} type={alertData.type} onClose={() => setAlertData({ ...alertData, open: false })} />
    </Box>
  );
};

const Divider = ({ sx }) => <Box sx={{ height: '1px', bgcolor: 'divider', ...sx }} />;

export default RewardApprove;
